import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore, DocumentSnapshot } from '@angular/fire/compat/firestore';
import { Observable, combineLatest } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';

export interface Topic {
  id: string;
  name: string;
  index: number;
  managerIds: any;
}

export interface Comment {
  authorId: string;
  commentId: string;
  author: string;
  text: string;
  timestamp: Date | { seconds: number; nanoseconds: number };
  formattedTimestampComment?: string;
}

export interface TranslatedContent {
  en: string;
  es: string;
}

export interface ForumPost {
  id: string;
  title: TranslatedContent | string;
  author: string;
  authorId: string;
  fixed?: boolean;
  description: TranslatedContent | string;
  likes: any;
  timestamp: Date | string | { seconds: number; nanoseconds: number };
  comments: number;
  commentsDetail: Comment[];
  isExpanded?: boolean;
  liked?: boolean;
  formattedTimestamp?: string;
}

@Injectable({
  providedIn: 'root'
})
export class FirestoreService {

  constructor(
    private firestore: AngularFirestore,
    public afAuth: AngularFireAuth) {
  }

  ///// CHAT /////
  // Retrieves the friends list with their conversation data
  getFriendsList(currentUserID: string) {
    return this.firestore.collection(`chatUsers/${currentUserID}/friends`).snapshotChanges().pipe(
      map(actions => actions.map(a => {
        const data = a.payload.doc.data() as { [key: string]: any };
        const id = a.payload.doc.id;
        return { id, currentUserID, ...data };
      }))
    );
  }


  getUnreadMessages(currentUserId: string) {
    return this.firestore.doc(`chatUsers/${currentUserId}`).valueChanges().pipe(
      map((userData: any) => {
        if (userData && userData.newMessages) {
          return userData.newMessages;
        } else {
          return undefined;
        }
      })
    );
  }

  getFriendsWithUnreadMessages(currentUserID: string) {
    return this.firestore.collection(`chatUsers/${currentUserID}/friends`).snapshotChanges().pipe(
        map(actions => actions.filter(a => {
            const data = a.payload.doc.data() as { newReceivedMessages?: number };
            return data.newReceivedMessages && data.newReceivedMessages > 0;
          })
          .map(a => a.payload.doc.id)
        )
      );
  }

  // Starts a new conversation
  createNewConversation(friendUser: { id: string, friendName: string }, currentUser: { id: string, displayName: string }) {
    const newChat = {
      userIds: [currentUser.id, friendUser.id],
      messages: [],
      createdAt: new Date()
    };
    const chatRef = this.firestore.collection('chats').add(newChat);
    return chatRef.then(docRef => {

      this.firestore.collection(`chatUsers`).doc(currentUser.id).set({ newMessages: 0 });

      // Add the new chat reference to both users' 'friends' subcollection
      this.firestore.collection(`chatUsers/${currentUser.id}/friends`).doc(friendUser.id).set({
        chatId: docRef.id,
        friendId: friendUser.id,
        friendName: friendUser.friendName,
        timestamp: new Date(),
        newReceivedMessages: 0
      });
      this.firestore.collection(`chatUsers/${friendUser.id}/friends`).doc(currentUser.id).set({
        chatId: docRef.id,
        friendId: currentUser.id,
        friendName: currentUser.displayName,
        timestamp: new Date(),
        newReceivedMessages: 0
      });
      return docRef.id;
    });
  }

  // async countNewMessagesPerChat(loggedUserId: string, friendUserId: string) {
  //   const friendRef = this.firestore.collection(`chatUsers/${friendUserId}/friends`).doc(loggedUserId).ref;
  //   await this.firestore.firestore.runTransaction(async transaction => {
  //     const friendDoc = await transaction.get(friendRef);
  //     if (!friendDoc.exists) {
  //       transaction.set(friendRef, { newReceivedMessages: 1 });
  //     } else {
  //       const data = friendDoc.data() as { newReceivedMessages?: number };
  //       const newReceivedMessages = data.newReceivedMessages || 0;
  //       transaction.update(friendRef, { newReceivedMessages: newReceivedMessages + 1 });
  //     }
  //   });
  // }

  // async countNewMessagesTotal(friendUserId: string) {
  //   const currentUserRef = this.firestore.collection(`chatUsers`).doc(friendUserId).ref;
  //   await this.firestore.firestore.runTransaction(async transaction => {
  //     const currentUserDoc = await transaction.get(currentUserRef);
  //     if (!currentUserDoc.exists) {
  //       throw new Error(`El documento del usuario actual con ID ${friendUserId} no existe.`);
  //     }
  //     const data = currentUserDoc.data() as { newMessages?: number } || {};
  //     const newMessages = data.newMessages || 0;
  //     transaction.update(currentUserRef, { newMessages: newMessages + 1 });
  //   });

  //   const userSnapshot = await currentUserRef.get();
  //   const userData = userSnapshot.data() as { newMessages?: number };
  //   if (!userSnapshot.exists || !userData.newMessages) {
  //     await currentUserRef.set({ newMessages: 0 }, { merge: true });
  //   }
  // }

  connectToChat(chatId: string) {
    return this.firestore.collection('chats').doc(chatId).valueChanges();
  }

  async sendMessage(friend: any, message: any) {
    return this.firestore.collection('chats').doc(friend.chatId).collection('messages').add(message).then((res) => {
      console.log(res);
      this.markAsUnread(friend.currentUserID, friend.id);

    });
  }

  async markAsUnread(loggedUserId: string, friendUserId: string) {
    try {
      // Add number of total unread messages
      const userDocRef = this.firestore.collection(`chatUsers`).doc(friendUserId);
      const userDocSnapshot = await userDocRef.get().pipe(first()).toPromise();
      if (userDocSnapshot.exists) {
        const data = userDocSnapshot.data();
        userDocRef.ref.update({ newMessages: (data["newMessages"] || 0) + 1 });
      }

      // Add number of specific chat unread messages
      const friendDocRef = this.firestore.collection(`chatUsers/${friendUserId}/friends`).doc(loggedUserId);
      const friendDocSnapshot = await friendDocRef.get().pipe(first()).toPromise();
      if (friendDocSnapshot.exists) {
        const data = friendDocSnapshot.data();
        friendDocRef.ref.update({ newReceivedMessages: (data["newReceivedMessages"] || 0) + 1 });
      }
    }
    catch (error) {
      console.error('Error reading or updating document:', error);
    }
  }

  getMessages(friend: any) {
    this.markAsRead(friend.currentUserID, friend.friendId, friend.newReceivedMessages);
    return this.firestore.collection('chats').doc(friend.chatId).collection('messages', ref => ref.orderBy('timestamp')).valueChanges();
  }

  async markAsRead(loggedUserId: string, friendUserId: string, newReceivedMessages: number) {
    try {
      // Deduct number of total unread messages
      const userDocRef = this.firestore.collection(`chatUsers`).doc(loggedUserId);
      const userDocSnapshot = await userDocRef.get().pipe(first()).toPromise();
      if (userDocSnapshot.exists) {
        const data = userDocSnapshot.data();
        userDocRef.ref.update({ newMessages: data["newMessages"] - newReceivedMessages });
      }

      // Reset number of specific chat unread messages
      this.firestore.collection(`chatUsers/${loggedUserId}/friends`).doc(friendUserId).update({ newReceivedMessages: 0 });
    }
    catch (error) {
      console.error('Error reading or updating document:', error);
    }
  }

  ///// NOTIFICATIONS /////
  getNotifications(currentUserID: string) {
    return this.firestore.collection(`notifications/${currentUserID}/messages`)
      .snapshotChanges()
      .pipe(
        map(actions => actions.map(a => {
          const data = a.payload.doc.data() as { [key: string]: any }; // Cast to object type
          const id = a.payload.doc.id;
          return { id, ...data };
        }))
      );
  }

  updateNotification(notificationId: string, currentUserID: string) {
    return this.firestore.collection('notifications').doc(currentUserID).collection('messages').doc(notificationId).update({ readAt: new Date() });
  }

  ///// FORUM /////
  // Method to get all topics
  getTopics(companyId: string): Observable<Topic[]> {
    return this.firestore.collection<Topic>('forumTopics', ref => ref.where('companyId', "==", companyId).orderBy('index', 'asc')).valueChanges();
  }

  // Method to create a new topic
  createTopic(topic: Omit<Topic, 'id'>): Promise<void> {
    const id = this.firestore.createId();
    return this.firestore.collection('topics').doc(id).set({ id, ...topic });
  }

  getForumPosts(topicId: string): Observable<any[]> {
    //return this.firestore.collection<ForumPost>('forumPosts', ref => ref.where('topicId', "==", topicId).orderBy('timestamp', 'desc')).valueChanges({ idField: 'id' });

    return this.firestore.collection('forumPosts', ref => ref.where('topicId', "==", topicId).orderBy('timestamp', 'desc')).snapshotChanges().pipe(
      switchMap(actions => {
        const postObservables = actions.map(a => {
          const data = a.payload.doc.data() as any;
          const id = a.payload.doc.id;

          console.log(id);
          return this.firestore.collection(`forumPosts`).doc(id).collection('likes').valueChanges().pipe(
            map(likesData => {
              return {
                id,
                title: data.title,
                author: data.author,
                authorId: data.authorId,
                fixed: data.fixed,
                description: data.description,
                likes: likesData || [],
                timestamp: data.timestamp,
                comments: data.comments,
                commentsDetail: data.commentsDetail || []
              } as any;
            })
          );
        });

        // Combine the array of observables into a single observable array
        return combineLatest(postObservables);
      })
    );

  }

  getForumPostById(id: string): Observable<ForumPost> {
    return this.firestore.collection<ForumPost>('forumPosts').doc(id).valueChanges();
  }

  getTutor(id: string): Observable<ForumPost> {
    return this.firestore.collection<ForumPost>('forumTutors').doc(id).valueChanges();
  }

  getTutortOfPostId(id: string): Observable<ForumPost> {
    return this.firestore.collection<ForumPost>('tutor').doc(id).valueChanges();
  }

  createForumPost(topicId: string, post: Omit<ForumPost, 'id'>): Promise<void> {
    const id = this.firestore.createId();
    return this.firestore.collection('forumPosts').doc(id).set({
      ...post,
      topicId: topicId,
      id,
      likes: 0, // Initialize likes
      comments: 0, // Initialize comments count
      commentsDetail: [], // Initialize comments array
      timestamp: new Date()
    });
  }

  updateForumPost(postId: string, post: Partial<ForumPost>): Promise<void> {
    return this.firestore.collection('forumPosts').doc(postId).update(post);
  }

  deleteForumPost(id: string): Promise<void> {
    return this.firestore.collection('forumPosts').doc(id).delete();
  }

  async addLike(user: any, postId: string): Promise<void> {
    try {
      const likesDocRef = this.firestore.collection(`forumPosts`)
        .doc(postId).collection('likes');
      const like = await likesDocRef.doc(user.id).get().pipe(first())
        .toPromise();
      if (like.exists) {
        await likesDocRef.doc(user.id).delete();
      } else {
        await likesDocRef.doc(user.id).set({ uid: user.id, displayName: user.author, createdAt: new Date() });
      }
    }
    catch (error) {
      console.error('Error reading or updating document:', error);
    }
  }

  addComment(postId: string, comment: Omit<Comment, 'commentId'>): Promise<void> {
    const postRef = this.firestore.collection('forumPosts').doc(postId).ref;
    const commentId = this.firestore.createId();
    const newComment = { commentId, ...comment, timestamp: new Date() };

    return this.firestore.firestore.runTransaction(async transaction => {
      const postDoc = await transaction.get(postRef);
      if (!postDoc.exists) {
        throw "Post does not exist!";
      }

      // Use a type assertion here to let TypeScript know the shape of postDoc.data()
      const postData = postDoc.data() as ForumPost;
      const commentsDetail = postData.commentsDetail || [];
      const newCommentsCount = postData.comments + 1 || 1;
      commentsDetail.push(newComment);

      transaction.update(postRef, { commentsDetail, comments: newCommentsCount });
    });
  }
}
